import { InfiniteData, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { ApiResponse, getCommentList, getGameCommentsList } from '~/api';
import {
  GetCommentData,
  GetCommentRequest,
  GetCommentsResponse,
} from '~/types';
import {
  GetGameCommentListRequest,
  GetGameCommentListResponse,
} from '~/types/missionGameApi';
import { MINUTE } from '~/constants';
import commentKey from './commentKey';

export const COMMENT_FETCH_SIZE = 30;

const useGetCommentListQuery = (
  request: GetCommentRequest | GetGameCommentListRequest,
  staleTime = 3 * MINUTE,
) => {
  return useSuspenseInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey:
      'game_id' in request
        ? commentKey.getGameCommentList(request)
        : commentKey.getCommentList(request),
    queryFn: ({ pageParam }) =>
      'game_id' in request
        ? getGameCommentsList({
            game_id: request.game_id,
            page: pageParam,
            sort_by: request.sort_by,
          })
        : getCommentList({
            character_id: request.character_id,
            page: pageParam,
            sort_by: request.sort_by,
          }),
    initialPageParam: 1,
    getNextPageParam: (response, _, lastPageParam) =>
      handleGetNextPageParam(response, lastPageParam),
    select: handleResponseSelect,
    staleTime,
  });
};

const handleGetNextPageParam = (
  response: ApiResponse<GetCommentsResponse | GetGameCommentListResponse>,
  lastPageParam: number,
) => {
  if (!response || !response.data) return undefined;
  if (response.data.length < COMMENT_FETCH_SIZE) return undefined;

  const nextPage = lastPageParam + 1;
  return response && response.data.length === 0 ? undefined : nextPage;
};

const handleResponseSelect = (
  response: InfiniteData<
    ApiResponse<GetCommentsResponse | GetGameCommentListResponse>,
    number
  >,
) => ({
  pages: response.pages.flatMap((page) => {
    if (page && page.data !== null) return page.data as GetCommentData[];
    return [];
  }),
  pageParams: response.pageParams,
  total:
    response.pages[0] && response.pages[0]?.data !== null
      ? (response.pages[0].data_additional?.total as number)
      : 0,
});

export default useGetCommentListQuery;
